/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Validators } from '@angular/forms';
import {
  AppFormArray,
  ApplicationStateHolder,
  BaseControlSchemaConfig, ControlSchemaPackedValue,
  ControlSchemaType,
  FormArraySchema,
  FormArraySchemaConfig,
} from '@gh/common-shared';
import { arrayDiff, denull } from '@gh/core-util';
import { get, isNil } from 'lodash';
import { AbstractBuilderControlSchema } from './abstract-builder-control-schema';
import { ensureControlSchema } from './builder-form-control-schema';
import { diffFormArray } from './form-diffs';

export class BuilderFormArraySchema extends AbstractBuilderControlSchema implements FormArraySchema {
  schemaType = ControlSchemaType.Array;

  children: AbstractBuilderControlSchema[] = [];

  constructor(stateHolder: ApplicationStateHolder) {
    super(stateHolder);
  }

  update(config: FormArraySchemaConfig): void {
    super.update(config);

    if (config.children) {
      arrayDiff(this.children, config.children, 'key', {
        onInsert: (schema, schemaConfig, newIndex) =>
          this.children.splice(newIndex, 0, this.updateOrCreateItem(schema, schemaConfig)),
        onIdentity: (schema, schemaConfig) => schema.update(schemaConfig),
        onDelete: (_, itemSchema, oldIndex) => this.children.splice(oldIndex, 1),
      });
    }
  }

  applyTo(control: AppFormArray, packedValue?: ControlSchemaPackedValue): void {
    diffFormArray(this, control, packedValue);

    super.applyTo(control);
  }

  build(value?: any): AppFormArray {
    const array = new AppFormArray(
      this,
      this.children.map((schema, i) => schema.build(get(value, i))),
      denull(Validators.compose(this.buildValidators())),
      denull(Validators.composeAsync(this.asyncValidators)),
    );

    array.required = this.isRequired();
    array.visible = this.isVisible();
    array.privileged = this.isPrivileged();
    array.lastValidators = this.validators;
    array.lastAsyncValidators = this.asyncValidators;

    if (!this.isEnabled()) {
      array.disable();
    }

    return array;
  }

  private updateOrCreateItem(schema: AbstractBuilderControlSchema,
                             schemaConfig?: BaseControlSchemaConfig): AbstractBuilderControlSchema {
    if (schema) {
      schema.update(schemaConfig || {});
      return schema;
    } else {
      return ensureControlSchema(this.stateHolder, schemaConfig);
    }
  }
}
